import type { ReactNode } from 'react';
import { GpsFixIcon } from '@phosphor-icons/react';
import { CommandGroup, CommandItem } from '@voll-intelligence/ui-kit/ui/command';
import { Spinner } from '@voll-intelligence/ui-kit/ui/spinner';
import { PLACE_ROW_CLASS } from './place_panel.js';

export type MyLocationState = 'idle' | 'loading' | 'error' | 'denied';

/** De onde veio o lugar de um campo, quando isso muda o que o host faz com ele.
 *  `geolocation` = o lugar real que a posição do navegador virou ("Minha
 *  localização"): o host não o grava como recente. Mora no VALOR do campo, e
 *  não num mapa à parte por id, porque o id não diz quem escolheu: a lista pode
 *  devolver o mesmo lugar que a posição deu. Regra das barras: todo pick da
 *  lista grava o lugar SEM origem; só o host, ao aplicar a posição, a grava. */
export type PlaceSource = 'geolocation';

export interface MyLocationRowCopy {
  /** A ação ("Minha localização"). */
  readonly label: string;
  /** Geolocation indisponível / ponto sem endereço: a dica na própria linha. */
  readonly errorLabel: string;
  /** Permissão recusada: a dica diz ONDE liberar (#4513, I6), como na corrida da
   *  99. Ausente ⇒ cai no `errorLabel`. */
  readonly deniedLabel?: string;
}

/**
 * Ação "perto de mim" — a primeira linha do painel de lugar na exibição
 * ZERO-QUERY, ao lado dos grupos de sugestão que o host passa via `groupLabel`
 * (ex.: "Recentes"/"Lugares favoritos" — o pacote é copy-agnostic e não sabe o nome
 * deles). A MESMA linha em todo campo de lugar das verticais (hotel, carro):
 * entra pelo `header` do `PlaceField`, ou direto num `CommandList` onde a
 * lista é montada à mão (o cartão do Onde da folha do hotel).
 *
 * Some por completo quando o host não passa `onUse`: ler a posição e trocá-la
 * por um lugar real é decisão do host (`useBrowserPosition` + o endpoint de
 * lugares por coordenada), então sem handler não há ação pra oferecer.
 *
 * Com query digitada ela também sai de cena, e isso corrige dois defeitos de
 * uma vez, não é estética. (a) Enter: o cmdk re-seleciona o item #0 a cada
 * mudança de busca (`setState('search') → selectFirstItem()`), então com a
 * linha em cena o realce ficava permanentemente nela e o Enter pedia
 * geolocation em vez de escolher o destino digitado. (b) `CommandEmpty` — o
 * spinner, o "nenhum lugar" e o alerta de erro — só renderiza com
 * `filtered.count === 0`, e sob `shouldFilter={false}` esse número é a
 * contagem de itens REGISTRADOS: a linha o mantinha em 1, e os três estados
 * nunca apareciam em produção, onde o host liga a ação sempre. O `PlaceField`
 * já esconde o `header` com termo; o `query` aqui é para quem monta a lista
 * por fora dele.
 */
export function MyLocationRow({
  copy,
  state = 'idle',
  query = '',
  onUse,
}: {
  readonly copy: MyLocationRowCopy;
  readonly state?: MyLocationState;
  readonly query?: string;
  readonly onUse?: () => void;
}): ReactNode {
  // As duas condições de existência da linha moram AQUI, não no JSX do pai:
  // sem handler não há ação pra oferecer, e com query digitada a linha não
  // pertence à exibição (ver o doc acima).
  if (!onUse || query.trim()) return null;
  return (
    <CommandGroup>
      {/* A mesma medida e tipografia das linhas de lugar do `PlaceResults` —
          esta é uma linha da mesma lista. */}
      <CommandItem value="__my_location__" onSelect={onUse} className={PLACE_ROW_CLASS}>
        <GpsFixIcon className="text-muted-foreground size-4 shrink-0" />
        <span className="grid min-w-0 flex-1 leading-tight">
          <span className="text-ui truncate text-foreground">{copy.label}</span>
          {(state === 'error' || state === 'denied') && (
            // A dica de permissão é uma frase com o caminho: quebra em vez de
            // cortar no meio dele.
            <span
              role="alert"
              className={`text-caption text-destructive ${state === 'denied' ? 'text-pretty' : 'truncate'}`}
            >
              {state === 'denied' ? (copy.deniedLabel ?? copy.errorLabel) : copy.errorLabel}
            </span>
          )}
        </span>
        {state === 'loading' && <Spinner aria-label={copy.label} />}
      </CommandItem>
    </CommandGroup>
  );
}
